import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Sparkles } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Sign In | Smartorange HUB";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);
      toast.success("Welcome back!");
      // Backend computes default_landing_path so allowlist-only users
      // (e.g. marketing-agency partners with role=team) land on the right
      // page instead of /dashboard or /my-payments. Marker: default-landing-path-v1
      navigate(userData?.default_landing_path || "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden flex">
      {/* Background effects */}
      <div className="fixed inset-0 bg-gradient-to-br from-orange-950/20 via-black to-black pointer-events-none" />
      <div className="fixed top-20 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
      <div className="fixed bottom-20 left-20 w-72 h-72 bg-orange-600/10 rounded-full blur-3xl animate-pulse delay-1000" />

      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center">
        <div className="relative z-10 text-center px-12">
          <BrandLogo className="h-16 mx-auto mb-8" />
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-zinc-50">SMARTORANGE</span>
            <br />
            <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
              HUB
            </span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-md mx-auto">
            Centralized platform for CGI project management, team coordination and finances
          </p>
          
          {/* Features list */}
          <div className="mt-12 space-y-4 text-left max-w-sm mx-auto">
            <div className="flex items-center gap-3 text-zinc-400">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-orange-400" />
              </div>
              <span>Project & task management</span>
            </div>
            <div className="flex items-center gap-3 text-zinc-400">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-orange-400" />
              </div>
              <span>Financial tracking & P&L reports</span>
            </div>
            <div className="flex items-center gap-3 text-zinc-400">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-orange-400" />
              </div>
              <span>24/7 AI support assistant</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <BrandLogo className="h-10 mb-2" />
            <span className="text-sm font-semibold text-zinc-300">HUB</span>
          </div>

          {/* Form Card */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-zinc-50 mb-2">Sign In</h2>
              <p className="text-zinc-500">Enter your credentials to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-50 placeholder:text-zinc-500 h-11"
                  required
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-zinc-50 placeholder:text-zinc-500 pr-10 h-11"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium group"
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Sign In
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link 
                to="/forgot-password" 
                className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <div className="mt-6 text-center">
              <Link 
                to="/" 
                className="text-sm text-zinc-500 hover:text-orange-400 transition-colors"
              >
                ← Back to home
              </Link>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-zinc-600 mt-8">
            © 2026 Smartorange HUB. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
